"use client";

import { useEffect, useRef, useState } from "react";

export default function CommercialCtaSection() {
    const ref = useRef<HTMLDivElement>(null);
    const [formData, setFormData] = useState({
        company: "",
        name: "",
        email: "",
        volume: "",
        message: "",
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const els = ref.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.08 }
        );
        els.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    function validate() {
        const e: Record<string, string> = {};
        if (!formData.company.trim()) e.company = "Company name is required.";
        if (!formData.name.trim()) e.name = "Contact name is required.";
        if (
            !formData.email.trim() ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        )
            e.email = "A valid business email is required.";
        if (!formData.volume.trim()) e.volume = "Indicate estimated annual volume.";
        return e;
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setErrors({});
        setSubmitting(true);
        await new Promise((r) => setTimeout(r, 1200));
        setSubmitting(false);
        setSubmitted(true);
    }

    function handleChange(
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "120px 0" }}
            aria-labelledby="commercial-cta-heading"
        >
            <div
                className="px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                {/* ── Left ── */}
                <div className="flex flex-col gap-8">
                        <div
                            className="flex items-center gap-3 reveal"
                            style={{ paddingTop: "8px" }}
                            >
                            <span
                                className="divider-red"
                                style={{ width: "32px" }}
                                aria-hidden="true"
                            />
                            <span className="type-subheading">Partner With Us</span>
                        </div>
                    <h2
                        id="commercial-cta-heading"
                        className="reveal reveal-delay-1"
                        style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: "clamp(2rem, 3.5vw, 3rem)",
                            fontWeight: 400,
                            color: "var(--color-white)",
                            lineHeight: 1.1,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Receive a term sheet
                        <br />
                        within two business days.
                    </h2>
                    <p
                        className="reveal reveal-delay-2"
                        style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "1rem",
                            color: "rgba(255,255,255,0.38)",
                            lineHeight: "1.7",
                        }}
                    >
                        Complete the form with your volume requirement and we will issue a
                        non-binding indicative term sheet, followed by a brief call with
                        our commercial team.
                    </p>

                    <div
                        className="reveal reveal-delay-3 flex flex-col gap-5 pt-4"
                        style={{ borderTop: "1px solid var(--color-white-divider)" }}
                    >
                        {[
                            {
                                icon: (
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                ),
                                label: "info@gfs-apac.com",
                            },
                            {
                                icon: (
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012.18 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.15a16 16 0 006.29 6.29l1.51-1.52a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                                    </svg>
                                ),
                                label: "+66 61 1133442",
                            },
                            {
                                icon: (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                  >
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                  </svg>
                                ),
                                label: `GFS SERVICES LTD.
                              Unit 2001-2005, Floor 20,
                              SJ Infinite 1 Business Complex,
                              349 Vibhavadi-Rangsit Road,
                              Chompon Sub-District,
                              Chatuchak,
                              Bangkok 10900 Thailand`,
                              },
                        ].map((item) => (
                            <div key={item.label} className="max-w-[400px] flex items-start gap-3">
                                <span style={{ color: "var(--color-red)", marginTop: "2px" }}>
                                    {item.icon}
                                </span>
                                <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.9375rem",
                                    color: "var(--color-white)",
                                    whiteSpace: "pre-line",   // 👈 THIS is the fix
                                    lineHeight: 1.6,          // 👈 better spacing
                                }}
                                >
                                {item.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Right: Form ── */}
                <div className="reveal reveal-delay-2">
                    {submitted ? (
                        <div
                            style={{
                                background: "var(--color-navy",
                                border: "1px solid var(--color-navy-light)",
                                borderRadius: "4px",
                                padding: "48px 40px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "16px",
                            }}
                        >
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#b91c1c"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <h3
                                style={{
                                    fontFamily: "var(--font-serif)",
                                    fontSize: "1.5rem",
                                    color: "var(--color-white)",
                                    fontWeight: 400,
                                }}
                            >
                                Enquiry received.
                            </h3>
                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.9375rem",
                                    color: "var(--color-white)",
                                    lineHeight: "3",
                                }}
                            >
                                Our commercial team will respond with an indicative term sheet
                                within two business days.
                                <br />
                                If your enquiry is urgent, please call : <p style={{ color: "var(--color-red)" }}>+66 61 1133442</p>
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            noValidate
                            aria-label="Request a proposal"
                            style={{
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: "4px",
                                padding: "40px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "20px",
                            }}
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                {/* Company */}
                                <div className="form-field">
                                    <label htmlFor="company" className="form-label" style={{ color: "var(--color-white)" }}>
                                        Company
                                    </label>
                                    <input
                                        id="company"
                                        name="company"
                                        type="text"
                                        autoComplete="organization"
                                        placeholder="Acme Refinery Ltd."
                                        value={formData.company}
                                        onChange={handleChange}
                                        className={`form-input${errors.company ? " error" : ""}`}
                                        aria-describedby={
                                            errors.company ? "company-error" : undefined
                                        }
                                        aria-invalid={!!errors.company}
                                    />
                                    {errors.company && (
                                        <span
                                            id="company-error"
                                            className="form-error"
                                            role="alert"
                                        >
                                            {errors.company}
                                        </span>
                                    )}
                                </div>

                                {/* Name */}
                                <div className="form-field">
                                    <label htmlFor="name" className="form-label" style={{ color: "var(--color-white)" }}>
                                        Contact Name
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        autoComplete="name"
                                        placeholder="James Whitfield"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`form-input${errors.name ? " error" : ""}`}
                                        aria-describedby={errors.name ? "name-error" : undefined}
                                        aria-invalid={!!errors.name}
                                    />
                                    {errors.name && (
                                        <span id="name-error" className="form-error" role="alert">
                                            {errors.name}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Email */}
                            <div className="form-field">
                                <label htmlFor="email" className="form-label" style={{ color: "var(--color-white)" }}>
                                    Business Email
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder="j.whitfield@acmerefinery.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`form-input${errors.email ? " error" : ""}`}
                                    aria-describedby={errors.email ? "email-error" : undefined}
                                    aria-invalid={!!errors.email}
                                />
                                {errors.email && (
                                    <span id="email-error" className="form-error" role="alert">
                                        {errors.email}
                                    </span>
                                )}
                            </div>

                            {/* Volume */}
                            <div className="form-field">
                                <label htmlFor="volume" className="form-label" style={{ color: "var(--color-white)" }}>
                                    Estimated Annual Volume (mt)
                                </label>
                                <select
                                    id="volume"
                                    name="volume"
                                    value={formData.volume}
                                    onChange={handleChange}
                                    className={`form-input${errors.volume ? " error" : ""}`}
                                    aria-describedby={errors.volume ? "volume-error" : undefined}
                                    aria-invalid={!!errors.volume}
                                    style={{ cursor: "pointer", color: formData.volume ? "var(--color-white)" : "rgba(255,255,255,0.25)" }}
                                >
                                    <option value="" disabled>
                                        Select volume range
                                    </option>
                                    <option value="500–1000">500 – 1,000 mt/yr</option>
                                    <option value="1000–2500">1,000 – 2,500 mt/yr</option>
                                    <option value="2500–5000">2,500 – 5,000 mt/yr</option>
                                    <option value="5000+">5,000+ mt/yr (discuss)</option>
                                </select>
                                {errors.volume && (
                                    <span id="volume-error" className="form-error" role="alert">
                                        {errors.volume}
                                    </span>
                                )}
                            </div>

                            {/* Message */}
                            <div className="form-field">
                                <label htmlFor="message" className="form-label" style={{ color: "var(--color-white)" }}>
                                    Additional Notes{" "}
                                    <span
                                        style={{ color: "rgba(255,255,255,0.25)", fontWeight: 400 }}
                                    >
                                        (optional)
                                    </span>
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={4}
                                    placeholder="Grade requirements, preferred incoterms, delivery destination, timeline..."
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="form-input"
                                    style={{ resize: "vertical", minHeight: "96px" }}
                                />
                            </div>

                            <button
                                type="submit"
                                className="btn-primary"
                                style={{ justifyContent: "center", marginTop: "4px" }}
                                disabled={submitting}
                                aria-busy={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            style={{
                                                animation: "spin 1s linear infinite"
                                            }}
                                            aria-hidden="true"
                                        >
                                            <path d="M21 12a9 9 0 11-6.219-8.56" />
                                        </svg>
                                        Submitting…
                                    </>
                                ) : (
                                    <>
                                        Submit Enquiry
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                    </>
                                )}
                            </button>

                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.75rem",
                                    color: "rgba(255,255,255,0.25)",
                                    lineHeight: "1.5",
                                }}
                            >
                                By submitting this form you consent to GFS Service Ltd.
                                contacting you regarding your enquiry. We do not share your
                                data with third parties.
                            </p>
                        </form>
                    )}
                </div>
            </div>

            <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
        </section>
    );
}